// Staff Igloo keys, as returned by GET /igloowork/staff/:userId/devices.
//
// One endpoint serves two views. A staff member reading their own keys gets a decrypted BLE `key`;
// a manager (Administrator/Manager/PropertyManager/ShiftManager) gets the same rows with `key`
// stripped. The web dashboard is only ever the manager view, so the normalizer drops `key`
// unconditionally — there is no reason to carry a live door credential through the query cache.
//
// There is no status field and no isConnected flag on this endpoint: an empty array is the only
// "not connected" signal the backend gives. See the ambiguity note in IglooWorkStaffSection.
export type IglooStaffKey = {
  id: string
  name: string
  startDate?: number
  endDate?: number
}

type RawIglooStaffKey = {
  id: string | number
  name: string
  startDate?: number
  endDate?: number
  key?: string
}

export type RawIglooStaffDevices = RawIglooStaffKey[] | null | undefined

export type IglooStaffDevices = { keys: IglooStaffKey[] }

const toNumberOrUndefined = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined

export const normalizeIglooStaffDevices = (raw: RawIglooStaffDevices): IglooStaffDevices => ({
  keys: (Array.isArray(raw) ? raw : []).map((k) => ({
    endDate: toNumberOrUndefined(k.endDate),
    id: String(k.id),
    name: String(k.name ?? ''),
    startDate: toNumberOrUndefined(k.startDate),
  })),
})

// The building's Igloo doors — the option list for the assignment picker.
// GET /igloowork/building/:buildingId/devices returns a flat [{ id, name }].
export type IglooBuildingDevice = { id: string; name: string }

type RawIglooBuildingDevice = { id: string | number; name: string }

export type RawIglooBuildingDevices = RawIglooBuildingDevice[] | null | undefined

export type IglooBuildingDevices = { devices: IglooBuildingDevice[] }

export const normalizeIglooBuildingDevices = (
  raw: RawIglooBuildingDevices,
): IglooBuildingDevices => ({
  devices: (Array.isArray(raw) ? raw : []).map((d) => ({
    id: String(d.id),
    name: String(d.name ?? ''),
  })),
})

// One door in the assign-doors (PUT) response.
//
// Two backend shapes are in play and both must parse. Before chitowntrainer-sails#6346 a per-door
// mint/revoke failure was audit-logged server-side and the door was simply OMITTED from the array,
// so failure could only be inferred by reconciling the request against the response. #6346 keeps
// the door in the array and marks it instead:
//
//   result: 'failed'
//   reason: mint_failed | mint_unconfirmed | revoke_failed | expiry_update_failed
//           | duplicate_key | unknown_device
//   held:   whether the member can still open that door
//
// `held` is the load-bearing field: a failed revoke leaves access in place, a failed mint does not.
// Reading `result`/`held` when present and falling back to the absence diff when they are not keeps
// this correct on both sides of that deploy — see getFailedDoorIds.
export type IglooAssignedDoor = {
  id: string
  name: string
  failed: boolean
  /** Undefined on the pre-#6346 shape, where the response carried no per-door outcome. */
  held?: boolean
  reason?: string
}

type RawIglooAssignedDoor = {
  id: string | number
  name?: string
  result?: string
  reason?: string
  held?: boolean
}

export type RawIglooAssignmentResult = RawIglooAssignedDoor[] | null | undefined

export const normalizeIglooAssignmentResult = (
  raw: RawIglooAssignmentResult,
): IglooAssignedDoor[] =>
  (Array.isArray(raw) ? raw : []).map((d) => ({
    failed: d.result === 'failed',
    ...(typeof d.held === 'boolean' ? { held: d.held } : {}),
    id: String(d.id),
    name: String(d.name ?? ''),
    ...(typeof d.reason === 'string' && d.reason ? { reason: d.reason } : {}),
  }))

// The doors the member can actually open once the call settles. A door the backend marked
// `held: false` is not one of them even though it is present in the response.
export const getHeldDoorIds = (doors: IglooAssignedDoor[]): string[] =>
  doors.filter((door) => door.held !== false).map((door) => door.id)

// Mirrors the backend's inheritance rule for the assign-doors call: when `expiresAt` is omitted,
// a newly-added door inherits the member's SHORTEST current expiry (falling back to the default
// 365-day window only if they hold no keys yet). Prefilling the date picker with this value means
// the field shows what an untouched save would actually persist, rather than a guess.
export const getShortestExpiry = (keys: IglooStaffKey[]): number | undefined => {
  const expiries = keys.map((k) => k.endDate).filter((e): e is number => typeof e === 'number')
  return expiries.length ? Math.min(...expiries) : undefined
}
